import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { usePenVisibilityActions } from 'features/timeSeries/hooks/usePenVisibilityActions'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { Bell, BellOff, Eye, EyeOff, Info } from 'lucide-react'
import React from 'react'

/** 'toolbar' renders the chart-level alerts bell; 'pane' the pen toggles,
    which need their controlled descriptions state. */
type PenDisplayActionsProps =
  | { appearance?: 'toolbar' }
  | {
      appearance: 'pane'
      showDescriptions: boolean
      onToggleDescriptions: () => void
    }

export const PenDisplayActions: React.FC<PenDisplayActionsProps> = (props) => {
  const { t } = useLingui()

  const { hideAllAlerts, setHideAllAlerts } = useScatterplotContext()
  const { areAllHidden, setAllVisibility } = usePenVisibilityActions()

  if (props.appearance === 'pane') {
    const { showDescriptions, onToggleDescriptions } = props
    return (
      <>
        <ChartTooltip
          label={areAllHidden ? t`Show all pens` : t`Hide all pens`}
          side='bottom'>
          <Button
            variant='ghostMuted'
            size='icon-xs'
            aria-label={areAllHidden ? t`Show all pens` : t`Hide all pens`}
            onClick={() => setAllVisibility(areAllHidden)}>
            {areAllHidden ? <EyeOff size={12} /> : <Eye size={12} />}
          </Button>
        </ChartTooltip>
        <ChartTooltip
          label={showDescriptions ? t`Hide descriptions` : t`Show descriptions`}
          side='bottom'>
          <Button
            variant='ghostMuted'
            size='icon-xs'
            pressed='accent'
            aria-pressed={showDescriptions}
            aria-label={
              showDescriptions ? t`Hide descriptions` : t`Show descriptions`
            }
            onClick={onToggleDescriptions}>
            <Info size={12} />
          </Button>
        </ChartTooltip>
      </>
    )
  }

  return (
    <ChartTooltip
      label={hideAllAlerts ? t`Show all alerts` : t`Hide all alerts`}
      side='bottom'>
      <Button
        variant='inputRaised'
        size='icon-lg'
        aria-label={hideAllAlerts ? t`Show all alerts` : t`Hide all alerts`}
        onClick={() => setHideAllAlerts((current) => !current)}>
        {hideAllAlerts ? (
          <BellOff size={18} strokeWidth={1.5} />
        ) : (
          <Bell size={18} strokeWidth={1.5} />
        )}
      </Button>
    </ChartTooltip>
  )
}
